import { useCallback, useContext, useMemo, useState } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import {
  useDeleteReportMutation,
  useGetBulkReportChildren,
  useRegenerateBulkReportZipMutation,
} from '@/modules/common/queries/reports.ts';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerAddAnnouncementModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddAnnouncementModal.tsx';
import { DisclosureListItem, Icon, ModalProviderContext, Spinner, TooltipNew } from 'ui';
import { DownloadIcon, FolderIcon, SendGreyIcon, TrashGrayIcon, TrashIcon } from 'ui/icons';
import { fixNameEnum, openInNewTab } from 'ui/utils';
import { RegisterClass, ReportFormat, ReportStatus, ReportWithUrlResponse } from 'api';

export interface ReportCardProps {
  report: ReportWithUrlResponse;
  canDelete?: boolean;
  isNested?: boolean;
  isLastBulkChild?: boolean;
  parentBulkReportId?: number;
  parentBulkReportRegisterId?: number;
  sendingRegisterClass?: RegisterClass;
}

export const ReportCard = ({
  report,
  canDelete = true,
  isNested = false,
  isLastBulkChild = false,
  parentBulkReportId,
  parentBulkReportRegisterId,
  sendingRegisterClass,
}: ReportCardProps) => {
  const {
    id,
    generated_at,
    status,
    filename,
    type,
    entity_id,
    format,
    file_storage_item_url,
    created_by,
    register_id,
    classes,
    is_bulk,
  } = report;
  const { openModal } = useContext(ModalProviderContext);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const isAllowedDeletion = !!created_by;
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { mutateAsync: deleteReport, isLoading: isReportDeletion } = useDeleteReportMutation();
  const { mutateAsync: regenerateBulkReportZip, isLoading: isBulkZipRegeneration } =
    useRegenerateBulkReportZipMutation();
  const { data: bulkChildrenData, isLoading: isBulkChildrenLoading } = useGetBulkReportChildren(
    id,
    register_id,
    !is_bulk || !isBulkOpen,
  );

  const handleReportDelete = async () => {
    await deleteReport({
      reportId: id,
      parentBulkReportId,
      parentBulkReportRegisterId,
      skipBulkChildrenInvalidation: isNested && isLastBulkChild,
    });
  };

  const isStaleBulkReport = is_bulk && status === ReportStatus.Stale;

  const handleDownloadReport = async () => {
    if (!isStaleBulkReport) return;

    const response = await regenerateBulkReportZip({ reportId: id, registerId: register_id });
    openInNewTab(response.file_storage_item_url, false);
  };

  const handleOpenCommunicationModal = useCallback(() => {
    openModal(IssuerAddAnnouncementModal, {
      reports: [
        {
          label: filename,
          value: { id, name: filename, type: type, entity_id, classes },
        },
      ],
      registerClass: sendingRegisterClass
        ? {
            value: sendingRegisterClass.id,
            label: sendingRegisterClass.class_name,
          }
        : undefined,
    });
  }, [classes, entity_id, filename, id, openModal, sendingRegisterClass, type]);

  const reportActions = (
    <div className="flex gap-2 select-none">
      <button onClick={handleOpenCommunicationModal}>
        <img
          src={SendGreyIcon}
          alt="send"
          className="hover:opacity-70 cursor-pointer min-w-5 w-5 h-5 min-h-5"
        />
      </button>
      <a
        href={isStaleBulkReport ? '#' : file_storage_item_url}
        rel="noreferrer"
        target={!is_bulk && format === ReportFormat.Pdf ? '_blank' : '_self'}
        className={
          isBulkZipRegeneration
            ? 'inline-flex items-center justify-center min-w-5 w-5 h-5 min-h-5'
            : undefined
        }
        onClick={async event => {
          if (!isStaleBulkReport) return;

          event.preventDefault();
          if (isBulkZipRegeneration) return;
          await handleDownloadReport();
        }}
      >
        {isBulkZipRegeneration ? (
          <Spinner size="xs" className="m-0" />
        ) : (
          <img
            src={DownloadIcon}
            className="hover:opacity-70 cursor-pointer min-w-5 w-5 h-5 min-h-5"
            alt="download"
          />
        )}
      </a>
      {canDelete && (
        <TooltipNew text="Unable to delete a system-generated report" disabled={isAllowedDeletion}>
          <button onClick={handleReportDelete} disabled={isReportDeletion || !isAllowedDeletion}>
            {isReportDeletion ? (
              <Spinner size="xs" className="m-0" />
            ) : (
              <img
                src={isAllowedDeletion ? TrashIcon : TrashGrayIcon}
                className="hover:opacity-70 min-w-5 w-5 h-5 min-h-5"
                alt="delete"
              />
            )}
          </button>
        </TooltipNew>
      )}
    </div>
  );

  const rightContent =
    status === ReportStatus.Generating ? (
      <div className="flex items-center bg-gray-200 rounded-xl px-3 py-1">
        <Spinner size="sm" className="grayscale" />
        <span className="text-xs font-medium">Generating</span>
      </div>
    ) : (
      <div onClick={event => event.stopPropagation()}>{reportActions}</div>
    );

  const leftSubduedContentSet = useMemo(() => {
    const res = [
      fixNameEnum(type)
        .replace(
          'Distribution',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        )
        .replace(
          'Dividend',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        ),
      is_bulk ? 'ZIP' : format.toUpperCase(),
    ];

    if (generated_at) {
      res.unshift(dateToPrint(generated_at));
    }

    return res;
  }, [format, generated_at, getRegisterTypeStrings, is_bulk, register_id, type]);

  const bulkChildren = bulkChildrenData?.data || [];
  const isBulkChildrenGenerationInProgress =
    is_bulk && status === ReportStatus.Generating && !bulkChildren.length;
  const bottomCustomContent = is_bulk ? (
    <div className="max-h-[400px] overflow-y-auto">
      {(isBulkChildrenLoading || isBulkChildrenGenerationInProgress) && (
        <div className="h-[120px] flex items-center justify-center border-t border-gray-200">
          <Spinner size="md" />
        </div>
      )}
      {!isBulkChildrenLoading &&
        !isBulkChildrenGenerationInProgress &&
        bulkChildren.map(childReport => (
          <ReportCard
            key={childReport.id}
            report={childReport}
            isNested={true}
            isLastBulkChild={bulkChildren.length === 1}
            parentBulkReportId={id}
            parentBulkReportRegisterId={register_id}
          />
        ))}
      {!isBulkChildrenLoading && !isBulkChildrenGenerationInProgress && !bulkChildren.length && (
        <div className="h-[120px] flex items-center justify-center border-t border-gray-200 text-sm text-gray-500">
          No reports found
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <DisclosureListItem
      leftContent={
        is_bulk ? (
          <div className="self-center shrink-0 w-[42px] h-[42px] flex justify-center items-center rounded-full bg-[rgba(229,_229,_229,_0.5)]">
            <Icon Source={FolderIcon} color="yellow" />
          </div>
        ) : undefined
      }
      leftMainString={filename
        ?.replace(
          'Distribution',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        )
        .replace(
          'Dividend',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        )}
      leftSubduedContentSet={leftSubduedContentSet}
      rightContent={rightContent}
      bottomCustomContent={bottomCustomContent}
      onOpen={is_bulk ? () => setIsBulkOpen(true) : undefined}
      classes={
        isNested
          ? {
              container: '!rounded-none !border-x-0 !border-b-0 !pt-[14px]',
            }
          : undefined
      }
    />
  );
};
